import { z } from 'zod';
import { moneyMinorSchema } from '../api';
import { admitCareTeamSchema } from './admission-care-team';
import { ADMISSION_PRIORITIES } from './admission-priority';
import { ICD10_CODE_PATTERN, NPHIES_DISCHARGE_DISPOSITIONS, NPHIES_INTENDED_STAY_CODES, diedMatchesStatus } from './admission-stay-codes';
import { ADMISSION_STATUS, BED_CLASS, BED_STATUS, ER_DISPOSITION, MEDICATION_ADMIN_STATUS, OT_CASE_STATUS, TRIAGE_LEVEL, } from '../enums';
export * from './admission-priority';
export * from './admission-stay-codes';
export const upsertWardSchema = z.object({
    branchId: z.string().uuid(),
    code: z.string().trim().toUpperCase().min(2).max(16),
    name: z.string().trim().min(2).max(160),
    departmentId: z.string().uuid().nullable().optional(),
    defaultBedClass: z.enum(BED_CLASS).default('general'),
    nurseStationPhone: z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, 'Enter a valid phone number').nullable().optional(),
    isActive: z.boolean().default(true),
});
export const updateWardSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    defaultBedClass: z.enum(BED_CLASS).optional(),
    nurseStationPhone: z.preprocess((v) => (v === '' ? null : v), z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, 'Enter a valid phone number').nullable().optional()),
    isActive: z.boolean().optional(),
});
export const upsertRoomSchema = z.object({
    wardId: z.string().uuid(),
    code: z.string().trim().toUpperCase().min(1).max(16),
    name: z.string().trim().max(120).optional(),
    isActive: z.boolean().default(true),
});
export const upsertBedSchema = z.object({
    roomId: z.string().uuid(),
    code: z.string().trim().toUpperCase().min(1).max(16),
    bedClass: z.enum(BED_CLASS),
    /** Daily bed charge item; drives the nightly auto-posting job (SRS 9.3). */
    chargeItemId: z.string().uuid().nullable().optional(),
    status: z.enum(BED_STATUS).default('vacant'),
    isActive: z.boolean().default(true),
});
export const updateBedSchema = upsertBedSchema.omit({ status: true }).partial();
export const setBedStatusSchema = z.object({
    status: z.enum(BED_STATUS),
    reason: z.string().trim().max(300).optional(),
});
export const admitPatientSchema = z.object({
    patientId: z.string().uuid(),
    bedId: z.string().uuid(),
    branchId: z.string().uuid().optional(),
    admittingDoctorId: z.string().uuid(),
    secondaryDoctorIds: z.array(z.string().uuid()).max(10).default([]),
    departmentId: z.string().uuid().nullable().optional(),
    source: z.enum(['opd', 'er', 'direct', 'transfer_in']).default('direct'),
    admissionPriority: z.enum(ADMISSION_PRIORITIES).optional(),
    intendedStayCode: z.enum(NPHIES_INTENDED_STAY_CODES).optional(),
    admittingSpecialtyId: z.string().uuid().nullable().optional(),
    encounterId: z.string().uuid().nullable().optional(),
    erCaseId: z.string().uuid().nullable().optional(),
    provisionalDiagnosis: z.string().trim().max(600).optional(),
    admissionNote: z.string().max(8000).optional(),
    estimatedStayDays: z.number().int().min(0).max(365).optional(),
    estimatedCostMinor: moneyMinorSchema.min(0).optional(),
    /** Advance deposit collected at the admission desk. */
    depositMinor: moneyMinorSchema.min(0).default(0),
    payerId: z.string().uuid().nullable().optional(),
    packageId: z.string().uuid().nullable().optional(),
    careTeam: admitCareTeamSchema.optional(),
});
export const transferBedSchema = z.object({
    toBedId: z.string().uuid(),
    reason: z.string().trim().max(400).optional(),
    transferredAt: z.string().datetime().optional(),
});
const dischargeFieldsSchema = z.object({
    status: z.enum(ADMISSION_STATUS).default('discharged'),
    dischargedAt: z.string().datetime().optional(),
    summary: z
        .object({
        finalDiagnosis: z.string().max(2000).optional(),
        procedures: z.string().max(4000).optional(),
        hospitalCourse: z.string().max(20000).optional(),
        conditionAtDischarge: z.string().max(1000).optional(),
        medicationsOnDischarge: z.string().max(6000).optional(),
        followUpInstructions: z.string().max(4000).optional(),
        followUpDate: z.string().datetime().nullable().optional(),
        keyResults: z.string().max(8000).optional(),
    })
        .optional(),
    /** Discharge is blocked until the final bill clears unless overridden. */
    overrideBillingClearance: z.boolean().default(false),
    overrideReason: z.string().max(400).optional(),
    dischargeDisposition: z.enum(NPHIES_DISCHARGE_DISPOSITIONS).optional(),
    dischargeSpecialtyId: z.string().uuid().nullable().optional(),
    causeOfDeathCode: z.string().trim().toUpperCase().regex(ICD10_CODE_PATTERN, 'Pick the cause of death from the ICD-10 list').optional(),
});
export const dischargeSchema = dischargeFieldsSchema
    .refine((value) => diedMatchesStatus(value.status, value.dischargeDisposition), {
    path: ['dischargeDisposition'],
    message: 'Died is the disposition of a patient who died, and of no one else',
})
    .refine((value) => value.causeOfDeathCode === undefined || value.status === 'expired', {
    path: ['causeOfDeathCode'],
    message: 'A coded cause of death is recorded only for a patient who died',
});
export const CPOE_ORDER_KINDS = ['medication', 'iv_fluid', 'diet', 'nursing', 'procedure'];
export const INVESTIGATION_ORDER_KINDS = ['lab', 'radiology'];
export const INVESTIGATION_KIND_REFUSAL = 'Order tests and imaging from the Investigations tab';
export const cpoeOrderSchema = z
    .object({
    admissionId: z.string().uuid(),
    kind: z.enum(CPOE_ORDER_KINDS, {
        errorMap: (issue, ctx) => ({
            message: issue.code === 'invalid_enum_value' && INVESTIGATION_ORDER_KINDS.includes(String(issue.received)) ? INVESTIGATION_KIND_REFUSAL : ctx.defaultError,
        }),
    }),
    productId: z.string().uuid().nullable().optional(),
    label: z.string().trim().min(1).max(240),
    dose: z.string().trim().max(80).optional(),
    route: z.string().trim().max(40).optional(),
    frequency: z.string().trim().max(60).optional(),
    /** Times of day the eMAR should generate doses for, e.g. ["08:00","20:00"]. */
    scheduleTimes: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)).max(12).default([]),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    durationDays: z.number().int().min(0).max(365).default(0),
    isHighAlert: z.boolean().default(false),
    isStat: z.boolean().default(false),
    instructions: z.string().max(1000).optional(),
})
    .refine((value) => !value.endsAt || !value.startsAt || Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: 'The order cannot end before it starts',
    path: ['endsAt'],
});
export const administerMedicationSchema = z.object({
    status: z.enum(MEDICATION_ADMIN_STATUS).default('given'),
    administeredAt: z.string().datetime().optional(),
    /** Second nurse signature, required for high-alert drugs. */
    witnessUserId: z.string().uuid().nullable().optional(),
    doseGiven: z.string().trim().max(80).optional(),
    site: z.string().trim().max(80).optional(),
    reason: z.string().trim().max(400).optional(),
    notes: z.string().max(1000).optional(),
});
export const nursingNoteSchema = z.object({
    admissionId: z.string().uuid(),
    kind: z.enum(['progress', 'handover', 'care_plan', 'incident']).default('progress'),
    shift: z.enum(['morning', 'evening', 'night']).optional(),
    /** SBAR handover fields (SRS 6.6). */
    situation: z.string().max(4000).optional(),
    background: z.string().max(4000).optional(),
    assessment: z.string().max(4000).optional(),
    recommendation: z.string().max(4000).optional(),
    body: z.string().max(8000).optional(),
});
export const progressNoteSchema = z.object({
    admissionId: z.string().uuid(),
    subjective: z.string().max(6000).optional(),
    objective: z.string().max(6000).optional(),
    assessment: z.string().max(6000).optional(),
    plan: z.string().max(6000).optional(),
    isRoundNote: z.boolean().default(false),
});
export const intakeOutputSchema = z.object({
    admissionId: z.string().uuid(),
    recordedAt: z.string().datetime().optional(),
    intakeMl: z.number().min(0).max(20000).default(0),
    intakeType: z.string().trim().max(80).optional(),
    outputMl: z.number().min(0).max(20000).default(0),
    outputType: z.string().trim().max(80).optional(),
    notes: z.string().max(600).optional(),
});
export const erRegisterSchema = z.object({
    /** Existing patient, or minimal details for an unknown/unconscious arrival. */
    patientId: z.string().uuid().nullable().optional(),
    unknownPatientLabel: z.string().trim().max(120).optional(),
    branchId: z.string().uuid().optional(),
    arrivalMode: z.enum(['walk_in', 'ambulance', 'police', 'referral']).default('walk_in'),
    chiefComplaint: z.string().trim().max(600).optional(),
    isMlc: z.boolean().default(false),
    mlcDetails: z.string().max(2000).optional(),
    broughtByName: z.string().trim().max(160).optional(),
    broughtByPhone: z.string().trim().max(20).optional(),
});
export const erTriageSchema = z.object({
    triageLevel: z.enum(TRIAGE_LEVEL),
    bayLabel: z.string().trim().max(40).optional(),
    notes: z.string().max(2000).optional(),
});
export const erDispositionSchema = z.object({
    disposition: z.enum(ER_DISPOSITION),
    notes: z.string().max(2000).optional(),
    causeOfDeath: z.string().trim().min(3).max(600).optional(),
    referredTo: z.string().trim().max(200).optional(),
    /** Populated by the server when the disposition creates an IPD admission. */
    admission: admitPatientSchema.partial().optional(),
});
export const scheduleOtCaseSchema = z.object({
    admissionId: z.string().uuid().nullable().optional(),
    patientId: z.string().uuid(),
    theatreId: z.string().uuid(),
    surgeonId: z.string().uuid(),
    anesthetistId: z.string().uuid().nullable().optional(),
    assistantIds: z.array(z.string().uuid()).max(10).default([]),
    procedureName: z.string().trim().min(2).max(300),
    chargeItemId: z.string().uuid().nullable().optional(),
    scheduledStart: z.string().datetime(),
    estimatedMinutes: z.number().int().min(10).max(1440).default(60),
    anesthesiaType: z.enum(['general', 'spinal', 'local', 'regional', 'sedation']).default('general'),
    notes: z.string().max(2000).optional(),
});
export const otCaseUpdateSchema = z.object({
    status: z.enum(OT_CASE_STATUS).optional(),
    preOpChecklist: z.record(z.string(), z.boolean()).optional(),
    whoChecklist: z.record(z.string(), z.boolean()).optional(),
    operativeNotes: z.string().max(20000).optional(),
    anesthesiaRecord: z.string().max(20000).optional(),
    postOpNotes: z.string().max(20000).optional(),
    spongeCountCorrect: z.boolean().optional(),
    instrumentCountCorrect: z.boolean().optional(),
    actualStart: z.string().datetime().nullable().optional(),
    actualEnd: z.string().datetime().nullable().optional(),
    consumables: z
        .array(z.object({
        productId: z.string().uuid(),
        stockBatchId: z.string().uuid().nullable().optional(),
        qty: z.number().min(0.001),
        isImplant: z.boolean().default(false),
        serialNo: z.string().trim().max(120).optional(),
    }))
        .optional(),
});
//# sourceMappingURL=ipd.js.map